import { io } from 'socket.io-client'
import { notification, Modal, Button } from 'ant-design-vue'
import { h } from 'vue'
import { getBackendUrlForWebSocket, getBackendUrl } from '../utils/backendUtils'

class AdminWebSocketService {
  constructor() {
    this.socket = null
    this.isConnected = false
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000
    this.orderNotificationCallbacks = []
    this.userNotificationCallbacks = []
    this.isMuted = false
    // 保存訂單取消請求彈窗實例
    this.cancelRequestModals = new Map() // orderId -> modal instance
  }

  // 設備識別碼：存在 localStorage，同一台機器的所有分頁共用（無痕視窗會取得新的一組）
  getDeviceId() {
    try {
      let deviceId = localStorage.getItem('admin_device_id')
      if (!deviceId) {
        deviceId = (crypto.randomUUID && crypto.randomUUID()) ||
          `d-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
        localStorage.setItem('admin_device_id', deviceId)
      }
      return deviceId
    } catch {
      return null
    }
  }

  // 被管理者踢出時清掉本分頁的登入狀態並回到登入頁
  handleForceLogout() {
    try {
      this.disconnect()
      sessionStorage.removeItem('admin_token')
      sessionStorage.removeItem('admin_user')
      notification.warning({
        message: '已被登出',
        description: '此設備已由管理者強制登出',
        duration: 5
      })
    } catch (error) {
      console.error('處理強制登出失敗:', error)
    } finally {
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login'
      }
    }
  }

  normalizeCancelOrderId(orderId) {
    return String(orderId)
  }

  notifyCancelRequestTodosChanged() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('cancel-request-todos-changed'))
    }
  }

  markCancelRequestSnoozed(orderId) {
    try {
      const normalizedOrderId = this.normalizeCancelOrderId(orderId)
      const snoozedMap = this.getSnoozedCancelRequests()
      snoozedMap[normalizedOrderId] = new Date().toISOString()
      localStorage.setItem('snoozedCancelRequests', JSON.stringify(snoozedMap))
    } catch (error) {
      console.error('標記稍後處理失敗:', error)
    }
  }

  clearSnoozedCancelRequest(orderId) {
    try {
      const normalizedOrderId = this.normalizeCancelOrderId(orderId)
      const snoozedMap = this.getSnoozedCancelRequests()
      if (snoozedMap[normalizedOrderId]) {
        delete snoozedMap[normalizedOrderId]
        localStorage.setItem('snoozedCancelRequests', JSON.stringify(snoozedMap))
      }
    } catch (error) {
      console.error('清除稍後處理標記失敗:', error)
    }
  }

  getSnoozedCancelRequests() {
    try {
      const stored = localStorage.getItem('snoozedCancelRequests')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  }

  // 連接 WebSocket
  connect(token) {
    if (this.socket && this.isConnected) {
      return Promise.resolve()
    }

    return new Promise((resolve, reject) => {
      try {
        // 使用環境變數配置的後端URL
        // 管理員 WebSocket 連接到前台 API 服務，因為訂單通知從那裡發送
        const backendUrl = getBackendUrlForWebSocket()

        this.socket = io(backendUrl, {
          auth: {
            token: token,
            deviceId: this.getDeviceId()
          },
          transports: ['websocket', 'polling'],
          timeout: 10000,
          forceNew: true,
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 5
        })

        // 設置連接超時
        const connectTimeout = setTimeout(() => {
          if (!this.isConnected) {
            this.socket?.disconnect()
            const error = new Error('WebSocket 連接超時')
            this.isConnected = false
            // 不直接 reject，而是嘗試重連
            this.attemptReconnect(token)
          }
        }, 15000)

        this.socket.on('connect', () => {
          clearTimeout(connectTimeout)
          this.isConnected = true
          this.reconnectAttempts = 0
          resolve()
        })

        this.socket.on('disconnect', (reason) => {
          clearTimeout(connectTimeout)
          this.isConnected = false

          // 如果是伺服器主動斷線或網路問題，嘗試重連
          if (reason === 'io server disconnect' || reason === 'transport close' || reason === 'transport error') {
            this.attemptReconnect(token)
          }
        })

        this.socket.on('connect_error', (error) => {
          clearTimeout(connectTimeout)
          this.isConnected = false
          
          // 不直接 reject，而是嘗試重連
          // 只有在達到最大重連次數時才 reject
          if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            reject(error)
          } else {
            this.attemptReconnect(token)
          }
        })

        this.socket.on('error', (error) => {
          // 忽略瀏覽器擴展相關的錯誤
          if (error && typeof error === 'object') {
            const errorMessage = error.message || String(error)
            // 過濾掉擴展相關的錯誤
            if (errorMessage.includes('FrameDoesNotExistError') || 
                errorMessage.includes('message port closed') ||
                errorMessage.includes('back/forward cache')) {
              return
            }
          }
        })

        // 監聽被管理者強制登出
        this.socket.on('force_logout', () => {
          this.handleForceLogout()
        })

        // 監聽新訂單通知
        this.socket.on('new_order_notification', (data) => {
          this.handleNewOrderNotification(data)
        })

        // 監聽新產品代尋請求通知
        this.socket.on('new_product_request_notification', (data) => {
          console.log('收到新產品代尋請求通知:', data)
          this.handleNewProductRequestNotification(data)
        })

        // 監聽訂單狀態更新通知
        this.socket.on('order_status_updated', (data) => {
          console.log('收到訂單狀態更新通知:', data)
          this.handleOrderStatusUpdate(data)
        })

        // 監聽訂單取消通知
        this.socket.on('order_cancelled_notification', (data) => {
          console.log('收到訂單取消通知:', data)
          this.handleOrderCancelledNotification(data)
        })

        // 監聽訂單取消請求通知
        this.socket.on('order_cancel_request_notification', (data) => {
          console.log('收到訂單取消請求通知:', data)
          this.handleOrderCancelRequestNotification(data)
        })

        // 監聽訂單取消請求處理完成通知
        this.socket.on('order_cancel_request_processed_notification', (data) => {
          console.log('收到訂單取消請求處理完成通知:', data)
          this.handleOrderCancelRequestProcessedNotification(data)
        })

        // 監聽訂單取消請求稍後通知（同步關閉其他管理員視窗彈窗）
        this.socket.on('order_cancel_request_snoozed_notification', (data) => {
          this.handleOrderCancelRequestSnoozedNotification(data)
        })

        // 監聽付款狀態更新通知
        this.socket.on('payment_status_updated', (data) => {
          console.log('收到付款狀態更新通知:', data)
          this.handlePaymentStatusUpdate(data)
        })

        // 監聽退貨申請通知
        this.socket.on('return_request_notification', (data) => {
          console.log('收到退貨申請通知:', data)
          this.handleReturnRequestNotification(data)
        })

        // 監聽一般訂單通知
        this.socket.on('order_notification', (data) => {
          console.log('收到訂單通知:', data)
          this.handleOrderNotification(data)
        })

        // 監聽用戶通知
        this.socket.on('user_notification', (data) => {
          console.log('收到用戶通知:', data)
          this.handleUserNotification(data)
        })

        // 監聽庫存異常通知
        this.socket.on('inventory_anomaly', (data) => {
          this.handleInventoryAnomaly(data)
        })

        // 已移除版本號不匹配事件的自動重整/提示處理

      } catch (error) {
        console.error('建立管理員 WebSocket 連接時發生錯誤:', error)
        reject(error)
      }
    })
  }

  // 嘗試重新連接
  attemptReconnect(token) {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      return
    }

    this.reconnectAttempts++

    setTimeout(() => {
      // 如果已經有連接，先斷開
      if (this.socket) {
        this.socket.disconnect()
        this.socket = null
      }
      
      this.connect(token).catch(() => {
        // 靜默處理重連失敗，讓重連機制繼續嘗試
      })
    }, this.reconnectDelay * this.reconnectAttempts)
  }

  // 處理新訂單通知
  handleNewOrderNotification(data) {
    const { order, message, timestamp } = data
    console.log('📨 WebSocket 收到新訂單通知:', data)
    console.log('📋 已註冊的回調數量:', this.orderNotificationCallbacks.length)

    // 顯示桌面通知
    this.showOrderNotification(order, message)

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach((callback, index) => {
      try {
        console.log(`🔄 執行回調 ${index + 1}/${this.orderNotificationCallbacks.length}`)
        callback(data)
      } catch (error) {
        console.error('執行訂單通知回調時發生錯誤:', error)
      }
    })
  }

  // 處理新產品代尋請求通知
  handleNewProductRequestNotification(data) {
    const { request, message, timestamp } = data

    // 顯示桌面通知
    this.showProductRequestNotification(request, message)

    // 執行註冊的回調函數（如果有的話）
    // 可以在這裡添加產品代尋通知的回調處理
  }

  // 處理一般訂單通知
  handleOrderNotification(data) {
    const { orderData, message, timestamp } = data

    // 顯示簡單通知
    notification.info({
      message: '訂單通知',
      description: message,
      duration: 5,
      placement: 'topRight'
    })

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('執行訂單通知回調時發生錯誤:', error)
      }
    })
  }

  // 處理訂單狀態更新通知
  handleOrderStatusUpdate(data) {
    const { message, updatedOrderIds, timestamp } = data

    // 顯示狀態更新通知（輕微提示，不干擾用戶）
    notification.info({
      message: '📋 訂單狀態已更新',
      description: message || '訂單狀態已根據配送方式自動更新',
      duration: 3,
      placement: 'bottomRight',
      style: {
        backgroundColor: '#f0f9ff',
        border: '1px solid #bae6fd'
      }
    })

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback({ type: 'status_update', ...data })
      } catch (error) {
        console.error('執行訂單狀態更新回調時發生錯誤:', error)
      }
    })
  }

  // 處理付款狀態更新通知
  handlePaymentStatusUpdate(data) {
    const { orderId, orderNumber, userId, paymentStatus, message, timestamp } = data
    console.log('付款狀態更新通知數據:', { orderId, orderNumber, userId, paymentStatus })

    // 檢查 orderNumber 是否為空或未定義
    if (!orderNumber) {
      console.warn('WebSocket通知中缺少orderNumber，將使用orderId:', orderId)
    }

    // 根據付款狀態顯示不同的通知
    let notificationMessage = '💰 付款狀態已更新'
    let description = message || '訂單付款狀態已更新'
    let notificationStyle = {
      backgroundColor: '#fff7e6',
      border: '1px solid #ffd591'
    }
    let duration = 5 // 預設5秒自動關閉

    if (paymentStatus === 3) {
      notificationMessage = '💰 客戶已通知付款'
      description = '客戶已通知完成付款，請確認後更新狀態'
      notificationStyle = {
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f'
      }
      duration = 8 // 客戶已付款通知8秒後自動消失
    } else if (paymentStatus === 1) {
      notificationMessage = '✅ 付款已確認'
      description = '訂單付款已確認完成'
      notificationStyle = {
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f'
      }
      duration = 8 // 已付款通知8秒後自動消失
    }

    // 顯示付款狀態更新通知
    notification.info({
      message: notificationMessage,
      description: `${description}\n點擊查看訂單詳情`,
      duration: duration,
      placement: 'topRight',
      style: notificationStyle,
      onClick: async () => {
        // 導航到訂單管理頁面並搜尋該訂單（使用訂單號碼）
        console.log('點擊通知導航:', { orderNumber, orderId })

        // 如果 orderNumber 有效且不等於 orderId，使用訂單號碼
        if (orderNumber && orderNumber.toString() !== orderId.toString()) {
          console.log('使用訂單號碼導航:', orderNumber)
          this.navigateToOrder(orderNumber)
        } else {
          // 如果 orderNumber 無效，嘗試從後端獲取正確的訂單號碼
          console.log('orderNumber無效，嘗試從後端獲取訂單號碼:', orderId)
          try {
            const response = await fetch(`${getBackendUrl()}/api/admin/orders/${orderId}`, {
              headers: {
                'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`
              }
            })
            if (response.ok) {
              const orderData = await response.json()
              const correctOrderNumber = orderData.order?.order_number
              if (correctOrderNumber) {
                console.log('從後端獲取到正確的訂單號碼:', correctOrderNumber)
                this.navigateToOrder(correctOrderNumber)
                return
              }
            }
          } catch (error) {
            console.error('獲取訂單詳情失敗:', error)
          }

          // 如果無法獲取訂單號碼，回退到使用訂單ID
          console.log('無法獲取訂單號碼，使用訂單ID導航:', orderId)
          this.navigateToOrderById(orderId)
        }
      }
    })

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback({ type: 'payment_update', ...data })
      } catch (error) {
        console.error('執行付款狀態更新回調時發生錯誤:', error)
      }
    })
  }

  // 處理退貨申請通知
  handleReturnRequestNotification(data) {
    const { returnRequestId, orderId, orderNumber, userId, totalRefundAmount, totalRefundPoints, message, timestamp } = data

    // 顯示退貨申請通知
    notification.warning({
      message: '📦 收到新的退貨申請！',
      description: `
        訂單編號：${orderNumber}
        退款金額：$${totalRefundAmount}
        退還紅利：${totalRefundPoints}點
        點擊查看詳情
      `,
      duration: 8, // 8秒後自動消失
      placement: 'topRight',
      style: {
        backgroundColor: '#fff2e8',
        border: '1px solid #ffbb96'
      },
      onClick: () => {
        // 可以在這裡添加跳轉到退貨申請詳情的邏輯
        console.log('點擊查看退貨申請詳情:', returnRequestId)
      }
    })

    // 播放提示音
    this.playNotificationSound()

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback({ type: 'return_request', ...data })
      } catch (error) {
        console.error('執行退貨申請通知回調時發生錯誤:', error)
      }
    })
  }

  // 處理庫存異常通知（右上角、紅色、不可關閉）
  handleInventoryAnomaly(data) {
    const {
      productId,
      productName,
      warehouseName,
      quantity,
      previousQuantity,
      newQuantity,
      movementTypeName,
      previousMovementNewQuantity,
      reasons = []
    } = data || {}

    const reasonText = reasons.length > 0 ? reasons.map((r, i) => `${i + 1}. ${r}`).join('\n') : '未知原因'
    const title = '⚠️ 庫存異常提醒'
    const descLines = [
      `商品：${productName || productId || '未知商品'}`,
      warehouseName ? `倉庫：${warehouseName}` : null,
      movementTypeName ? `異動類型：${movementTypeName}` : null,
      `期初：${previousQuantity}，異動數量：${quantity}，期末：${newQuantity}`,
      previousMovementNewQuantity != null ? `上一筆結餘：${previousMovementNewQuantity}` : null,
      '原因：',
      reasonText
    ].filter(Boolean)

    const key = `inventory_anomaly_${Date.now()}_${productId || ''}`
    notification.error({
      message: title,
      description: descLines.join('\n'),
      duration: 10, // 10秒後自動消失
      placement: 'topRight',
      style: {
        whiteSpace: 'pre-line',
        backgroundColor: '#fff1f0',
        border: '1px solid #ff4d4f',
        color: '#a8071a'
      },
      key
    })
  }

  // 處理訂單取消通知
  handleOrderCancelledNotification(data) {
    const { orderId, orderNumber, cancelledBy, cancelReason, finalAmount, message, timestamp } = data

          // 顯示訂單取消通知
      notification.warning({
        message: '❌ 訂單已被取消！',
        description: `
          訂單編號：${orderNumber || orderId}
          取消者：${cancelledBy === 'admin' ? '管理員' : '用戶'}
          取消原因：${cancelReason}
          取消時間：${new Date(timestamp).toLocaleString('zh-TW')}
          點擊查看詳情
        `,
        duration: 8, // 8秒後自動消失
        placement: 'topRight',
        style: {
          backgroundColor: '#fff1f0',
          border: '1px solid #ffccc7'
        },
        onClick: () => {
          // 導航到訂單管理頁面並搜尋該訂單
          this.navigateToOrder(orderNumber || orderId)
        }
      })

    // 播放提示音
    this.playNotificationSound()

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback({ type: 'order_cancelled', ...data })
      } catch (error) {
        console.error('執行訂單取消通知回調時發生錯誤:', error)
      }
    })
  }

  // 處理訂單取消請求通知
  handleOrderCancelRequestNotification(data) {
    console.log('🔔 處理訂單取消請求通知:', data)
    const { orderId, orderNumber, customerName, orderStatus, finalAmount, message, timestamp } = data

    try {
      // 顯示不可關閉的訂單取消請求彈窗
      console.log('🔔 準備顯示取消請求彈窗')
      this.showOrderCancelRequestModal(data)
      console.log('🔔 取消請求彈窗已顯示')
    } catch (error) {
      console.error('🔔 顯示取消請求彈窗時發生錯誤:', error)
    }

    // 播放提示音
    this.playNotificationSound()

    this.notifyCancelRequestTodosChanged()

    // 執行註冊的回調函數
    this.orderNotificationCallbacks.forEach(callback => {
      try {
        callback({ type: 'order_cancel_request', ...data })
      } catch (error) {
        console.error('執行訂單取消請求通知回調時發生錯誤:', error)
      }
    })
  }

  // 處理訂單取消請求處理完成通知
  handleOrderCancelRequestProcessedNotification(data) {
    console.log('🔔 處理訂單取消請求處理完成通知:', data)
    const orderId = this.normalizeCancelOrderId(data?.orderId)
    const { orderNumber, action } = data

    try {
      this.closeCancelRequestModal(orderId)

      // 移除待處理的取消請求
      this.removePendingCancelRequest(orderId)

      this.notifyCancelRequestTodosChanged()

      // 移除處理結果通知，因為不需要提示
      // 只記錄到控制台
      if (action === 'approved') {
        console.log(`訂單 ${orderNumber} 的取消請求已被其他管理員確認`)
      } else if (action === 'rejected') {
        console.log(`訂單 ${orderNumber} 的取消請求已被其他管理員拒絕`)
      }

      console.log(`🔔 已處理訂單取消請求處理完成通知: ${orderNumber} (${action})`)
    } catch (error) {
      console.error('🔔 處理訂單取消請求處理完成通知時發生錯誤:', error)
    }
  }

  // 處理其他管理員點擊「稍後通知」的同步通知
  handleOrderCancelRequestSnoozedNotification(data) {
    const orderId = this.normalizeCancelOrderId(data?.orderId)
    const orderNumber = data?.orderNumber
    if (!orderId) return

    this.closeCancelRequestModal(orderId)
    this.markCancelRequestSnoozed(orderId)
    this.notifyCancelRequestTodosChanged()
    console.log(`已同步關閉訂單 ${orderNumber || orderId} 的取消請求彈窗（稍後通知）`)
  }

  closeCancelRequestModal(orderId, modalInstance = null) {
    const normalizedOrderId = this.normalizeCancelOrderId(orderId)
    const modal = modalInstance || this.cancelRequestModals.get(normalizedOrderId)
    if (!modal) return

    try {
      modal.destroy()
    } catch (modalError) {
      console.error('關閉彈窗時發生錯誤:', modalError)
      try {
        modal.update({ visible: false })
      } catch (updateError) {
        console.error('隱藏彈窗時發生錯誤:', updateError)
      }
    }

    this.cancelRequestModals.delete(normalizedOrderId)
  }

  async postSnoozeCancelRequest(orderId, orderNumber) {
    const normalizedOrderId = this.normalizeCancelOrderId(orderId)
    const baseUrl = getBackendUrl()
    const requestInit = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`
      },
      body: JSON.stringify({ orderNumber })
    }

    const candidateUrls = baseUrl
      ? [
          `${baseUrl}/admin/orders/${normalizedOrderId}/snooze-cancel-request`,
          `${baseUrl}/api/admin/orders/${normalizedOrderId}/snooze-cancel-request`
        ]
      : [
          `/api/admin/orders/${normalizedOrderId}/snooze-cancel-request`,
          `/admin/orders/${normalizedOrderId}/snooze-cancel-request`
        ]

    let lastError = null
    for (const url of candidateUrls) {
      try {
        const response = await fetch(url, requestInit)
        if (response.ok) {
          return await response.json()
        }
        lastError = new Error(`HTTP ${response.status}`)
      } catch (error) {
        lastError = error
      }
    }

    throw lastError || new Error('同步稍後通知失敗')
  }

  // 處理用戶通知
  handleUserNotification(data) {
    const { userData, message, type } = data

    if (type === 'new_user') {
      // 顯示新用戶註冊通知
      this.showUserRegistrationNotification(userData, message)
    } else {
      // 顯示一般用戶通知
      notification.info({
        message: '用戶通知',
        description: message || '收到用戶相關通知',
        duration: 5,
        placement: 'topRight'
      })
    }

    // 執行註冊的回調函數
    this.userNotificationCallbacks.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error('執行用戶通知回調時發生錯誤:', error)
      }
    })
  }

  // 顯示新用戶註冊通知
  showUserRegistrationNotification(userData, message) {
    const username = userData?.username || '未知用戶'
    const fullName = userData?.full_name || userData?.fullName || '未知姓名'
    const email = userData?.email || '未知郵箱'

    notification.success({
      message: '🔔 有新用戶註冊！',
      description: `
        用戶名：${username}
        姓名：${fullName}
        郵箱：${email}
        點擊查看詳情
      `,
      duration: 10,
      placement: 'topRight',
      style: {
        backgroundColor: '#e6f7ff',
        border: '1px solid #91d5ff'
      },
      onClick: () => {
        // 可以在這裡添加跳轉到用戶詳情的邏輯
        console.log('點擊查看用戶詳情:', username)
      }
    })

    // 播放提示音
    this.playNotificationSound()
  }

  // 顯示新訂單通知
  showOrderNotification(order, message) {
    const orderInfo = order || {}
    const customerName = orderInfo.customerInfo?.name || '未知客戶'
    const orderNumber = orderInfo.orderNumber || '未知訂單號'
    const totalAmount = orderInfo.finalAmount || orderInfo.totalAmount || 0

    // 顯示新訂單通知
    notification.success({
      message: '🛒 收到新訂單！',
      description: `
        訂購人：${customerName}
        訂單編號：${orderNumber}
        訂單金額：$${totalAmount}
        點擊查看詳情
      `,
      duration: 8,
      placement: 'topRight',
      style: {
        backgroundColor: '#f6ffed',
        border: '1px solid #b7eb8f'
      },
      onClick: () => {
        // 導航到訂單管理頁面並搜尋該訂單
        this.navigateToOrder(orderNumber)
      }
    })

    // 播放提示音（如果瀏覽器支援）
    this.playNotificationSound()
  }

  // 導航到訂單詳情
  navigateToOrder(orderNumber) {
    try {
      // 使用 Vue Router 導航到訂單管理頁面
      if (window.vueRouter) {
        // 先導航到訂單頁面
        window.vueRouter.push('/orders').then(() => {
          // 延遲一下確保頁面載入完成，然後觸發搜尋
          setTimeout(() => {
            // 觸發全局事件來設定搜尋關鍵字
            window.dispatchEvent(new CustomEvent('searchOrder', {
              detail: { orderNumber }
            }))
          }, 500)
        })
      } else {
        // 如果沒有 router 實例，直接修改 URL
        window.location.href = `/orders?search=${orderNumber}`
      }
    } catch (error) {
      console.error('導航到訂單詳情失敗:', error)
    }
  }

  // 根據訂單ID導航到訂單詳情
  navigateToOrderById(orderId) {
    try {
      // 使用 Vue Router 導航到訂單管理頁面
      if (window.vueRouter) {
        // 先導航到訂單頁面
        window.vueRouter.push('/orders').then(() => {
          // 延遲一下確保頁面載入完成，然後觸發搜尋
          setTimeout(() => {
            // 觸發全局事件來設定搜尋關鍵字（使用訂單ID）
            window.dispatchEvent(new CustomEvent('searchOrder', {
              detail: { orderId }
            }))
          }, 500)
        })
      } else {
        // 如果沒有 router 實例，直接修改 URL
        window.location.href = `/orders?orderId=${orderId}`
      }
    } catch (error) {
      console.error('根據訂單ID導航到訂單詳情失敗:', error)
    }
  }

  // 顯示訂單取消請求彈窗
  showOrderCancelRequestModal(data) {
    const normalizedOrderId = this.normalizeCancelOrderId(data.orderId)
    const { orderNumber, customerName, finalAmount } = data

    // 檢查是否已經有相同訂單的彈窗存在
    if (this.cancelRequestModals.has(normalizedOrderId)) {
      console.log(`訂單 ${orderNumber} 的取消請求彈窗已存在，跳過重複顯示`)
      return
    }

    // 將取消請求保存到 localStorage
    this.savePendingCancelRequest({ ...data, orderId: normalizedOrderId })

    // 保存原始數據到實例變數中，供稍後通知使用
    this.pendingCancelRequestData = this.pendingCancelRequestData || new Map()
    this.pendingCancelRequestData.set(normalizedOrderId, { ...data, orderId: normalizedOrderId })

    let modal = null

    const clearModalState = () => {
      this.cancelRequestModals.delete(normalizedOrderId)
      this.pendingCancelRequestData?.delete(normalizedOrderId)
    }

    const destroyModal = () => {
      clearModalState()
      if (modal) {
        try {
          modal.destroy()
        } catch (error) {
          console.error('關閉取消請求彈窗失敗:', error)
        }
      }
    }

    // 使用自訂 footer，避免連續彈窗時 DOM querySelector 打到錯誤的 modal 而遺失「稍後通知」
    modal = Modal.confirm({
      title: '訂單取消請求',
      width: 500,
      centered: true,
      maskClosable: false,
      keyboard: false,
      closable: false,
      wrapClassName: `cancel-request-modal-${normalizedOrderId}`,
      content: h('div', [
        h('p', `客戶：${customerName}`),
        h('p', `訂單編號：${orderNumber}`),
        h('p', `訂單金額：NT$ ${finalAmount}`),
        h('p', '客戶請求取消此訂單，請選擇處理方式：')
      ]),
      footer: () => h('div', {
        style: {
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%'
        }
      }, [
        h(Button, {
          type: 'primary',
          onClick: () => {
            this.handleLaterNotification(normalizedOrderId, orderNumber, customerName, modal)
          }
        }, { default: () => '稍後通知' }),
        h('div', {
          style: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }
        }, [
          h(Button, {
            onClick: () => {
              destroyModal()
              this.showRejectConfirmModal(normalizedOrderId, orderNumber, customerName, data)
            }
          }, { default: () => '拒絕取消' }),
          h(Button, {
            type: 'primary',
            danger: true,
            onClick: () => {
              destroyModal()
              this.handleConfirmCancelOrder(normalizedOrderId, orderNumber, customerName)
            }
          }, { default: () => '確認取消訂單' })
        ])
      ])
    })

    this.cancelRequestModals.set(normalizedOrderId, modal)
    console.log(`已保存訂單 ${orderNumber} 的取消請求彈窗實例`)
  }

  // 顯示新產品代尋請求通知
  showProductRequestNotification(request, message) {
    const requestInfo = request || {}
    const userName = requestInfo.userName || '未知用戶'
    const requestId = requestInfo.id || '未知ID'
    const productDescription = requestInfo.productDescription || '無描述'

    // 截取產品描述的前50個字符
    const shortDescription = productDescription.length > 50
      ? productDescription.substring(0, 50) + '...'
      : productDescription

    // 顯示新產品代尋請求通知
    notification.info({
      message: '🔍 收到新產品代尋請求！',
      description: `
        請求編號：#${requestId}
        客戶：${userName}
        產品描述：${shortDescription}
        點擊查看詳情
      `,
      duration: 8,
      placement: 'topRight',
      style: {
        backgroundColor: '#e6f7ff',
        border: '1px solid #91d5ff'
      },
      onClick: () => {
        // 可以在這裡添加跳轉到產品代尋詳情的邏輯
        console.log('點擊查看產品代尋詳情:', requestId)
      }
    })

    // 播放提示音（如果瀏覽器支援）
    this.playNotificationSound()
  }

  // 處理音效 URL，確保在 HTTPS 環境下使用正確的協議
  normalizeAudioUrl(url) {
    if (!url) return null
    
    // 如果當前頁面是 HTTPS，但 URL 是 HTTP，轉換為相對路徑或 HTTPS
    if (window.location.protocol === 'https:') {
      try {
        const urlObj = new URL(url)
        // 如果是 HTTP 協議，轉換為相對路徑（使用當前域名）
        if (urlObj.protocol === 'http:') {
          // 如果是本地 IP 地址，使用相對路徑
          if (urlObj.hostname === '127.0.0.1' || urlObj.hostname === 'localhost') {
            return urlObj.pathname
          }
          // 否則轉換為 HTTPS
          return url.replace('http://', 'https://')
        }
      } catch (e) {
        // 如果 URL 解析失敗，可能是相對路徑，直接返回
        return url
      }
    }
    
    return url
  }

  // 播放通知音效
  async playNotificationSound() {
    if (this.isMuted) return
    try {
      // 獲取音效設定
      const audioSettings = await this.getAudioSettings()

      // 檢查是否啟用音效
      if (!audioSettings.enabled) {
        return
      }

      // 優先使用 API 返回的完整音效檔案 URL
      let audioUrl = null
      if (audioSettings.audio_url) {
        audioUrl = this.normalizeAudioUrl(audioSettings.audio_url)
      } else if (audioSettings.order_notification_audio) {
        // 如果沒有 audio_url，使用原有的邏輯作為後備
        // 在 HTTPS 環境下使用相對路徑，否則使用完整 URL
        if (window.location.protocol === 'https:') {
          audioUrl = `/uploads/audio/${audioSettings.order_notification_audio}`
        } else {
          const backendUrl = getBackendUrlForWebSocket()
          audioUrl = `${backendUrl}/uploads/audio/${audioSettings.order_notification_audio}`
        }
      }

      // 播放設定的音效檔案
      if (audioUrl) {
        const audio = new Audio(audioUrl)
        audio.volume = audioSettings.volume || 0.8

        audio.addEventListener('error', () => {
          // 如果音效檔案載入失敗，使用程式碼生成的音效作為後備
          this.playFallbackSound()
        })

        audio.play().catch(() => {
          // 如果播放失敗，使用程式碼生成的音效作為後備
          this.playFallbackSound()
        })
      } else {
        // 沒有設定音效檔案時，使用程式碼生成的音效
        this.playFallbackSound()
      }
    } catch (error) {
      // 如果獲取設定失敗，使用程式碼生成的音效作為後備
      this.playFallbackSound()
    }
  }

  // 獲取音效設定
  async getAudioSettings() {
    try {
      // 使用完整的URL路徑，確保請求能正確到達後端
      const backendUrl = getBackendUrlForWebSocket()
      const response = await fetch(`${backendUrl}/admin/settings/audio`, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const settings = await response.json()
        return settings
      } else {
        throw new Error(`獲取音效設定失敗: ${response.status}`)
      }
    } catch (error) {
      // 返回預設設定
      return {
        enabled: true,
        order_notification_audio: '',
        volume: 0.8
      }
    }
  }

  // 後備音效（程式碼生成的音效）
  playFallbackSound() {
    if (this.isMuted) return
    try {
      // 創建音頻上下文
      const audioContext = new (window.AudioContext || window.webkitAudioContext)()

      // 創建簡單的提示音
      const oscillator = audioContext.createOscillator()
      const gainNode = audioContext.createGain()

      oscillator.connect(gainNode)
      gainNode.connect(audioContext.destination)

      oscillator.frequency.setValueAtTime(800, audioContext.currentTime)
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.1)

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime)
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3)

      oscillator.start(audioContext.currentTime)
      oscillator.stop(audioContext.currentTime + 0.3)
    } catch (error) {
      console.error('無法播放後備音效:', error)
    }
  }

  // 設定音效靜音（只影響提示音效，不影響 UI 通知）
  setMuted(muted) {
    this.isMuted = !!muted
  }

  // 註冊新訂單通知回調
  onNewOrder(callback) {
    if (typeof callback === 'function') {
      this.orderNotificationCallbacks.push(callback)
    }
  }

  // 移除新訂單通知回調
  offNewOrder(callback) {
    const index = this.orderNotificationCallbacks.indexOf(callback)
    if (index > -1) {
      this.orderNotificationCallbacks.splice(index, 1)
    }
  }

  // 註冊用戶通知回調
  onUserNotification(callback) {
    if (typeof callback === 'function') {
      this.userNotificationCallbacks.push(callback)
    }
  }

  // 移除用戶通知回調
  offUserNotification(callback) {
    const index = this.userNotificationCallbacks.indexOf(callback)
    if (index > -1) {
      this.userNotificationCallbacks.splice(index, 1)
    }
  }

  // 監聽事件
  on(event, callback) {
    if (this.socket) {
      this.socket.on(event, callback)
    }
  }

  // 移除事件監聽
  off(event, callback) {
    if (this.socket) {
      this.socket.off(event, callback)
    }
  }

  // 斷開連接
  disconnect() {
    if (this.socket) {
      this.socket.disconnect()
      this.socket = null
      this.isConnected = false
      this.reconnectAttempts = 0
      this.orderNotificationCallbacks = []
      this.userNotificationCallbacks = []

      // 清理所有取消請求彈窗
      this.cancelRequestModals.forEach((modal, orderId) => {
        try {
          modal.destroy()
        } catch (error) {
          // 靜默處理清理錯誤
        }
      })
      this.cancelRequestModals.clear()
    }
  }

  // 檢查連接狀態
  getConnectionStatus() {
    return {
      isConnected: this.isConnected,
      socketId: this.socket?.id || null,
      reconnectAttempts: this.reconnectAttempts
    }
  }

  // 處理確認取消訂單
  async handleConfirmCancelOrder(orderId, orderNumber, customerName) {
    try {
      // 調用後台API取消訂單
      const response = await fetch(`/api/admin/orders/cancel-orders`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          orderIds: [orderId]
        })
      })

      const result = await response.json()

      if (result.success) {
        // 更新訂單的取消請求狀態為已確認
        try {
          await fetch(`/api/admin/orders/${orderId}/update-cancel-status`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`
            },
            body: JSON.stringify({
              status: 'approved'
            })
          })
        } catch (updateError) {
          console.error('更新取消請求狀態失敗:', updateError)
        }

        // 移除待處理的取消請求
        this.removePendingCancelRequest(orderId)
        this.clearSnoozedCancelRequest(orderId)
        this.notifyCancelRequestTodosChanged()

        notification.success({
          message: '✅ 訂單取消成功',
          description: `已成功取消訂單 ${orderNumber}`,
          duration: 5,
          placement: 'topRight'
        })
      } else {
        notification.error({
          message: '❌ 訂單取消失敗',
          description: result.message || '取消訂單時發生錯誤',
          duration: 8,
          placement: 'topRight'
        })
      }
    } catch (error) {
      console.error('取消訂單失敗:', error)
      notification.error({
        message: '❌ 訂單取消失敗',
        description: '網路錯誤，請稍後再試',
        duration: 8,
        placement: 'topRight'
      })
    }
  }

  // 保存待處理的取消請求到 localStorage
  savePendingCancelRequest(data) {
    try {
      const pendingRequests = this.getPendingCancelRequests()
      const normalizedOrderId = this.normalizeCancelOrderId(data.orderId)
      const requestKey = `cancel_request_${normalizedOrderId}`
      
      // 保存請求資料
      pendingRequests[requestKey] = {
        ...data,
        orderId: normalizedOrderId,
        timestamp: new Date().toISOString(),
        savedAt: new Date().toISOString()
      }
      
      localStorage.setItem('pendingCancelRequests', JSON.stringify(pendingRequests))
      console.log('已保存待處理的取消請求:', data.orderNumber)
      this.notifyCancelRequestTodosChanged()
    } catch (error) {
      console.error('保存待處理取消請求失敗:', error)
    }
  }

  // 獲取所有待處理的取消請求
  getPendingCancelRequests() {
    try {
      const stored = localStorage.getItem('pendingCancelRequests')
      return stored ? JSON.parse(stored) : {}
    } catch (error) {
      console.error('獲取待處理取消請求失敗:', error)
      return {}
    }
  }

  // 移除已處理的取消請求
  removePendingCancelRequest(orderId) {
    try {
      const normalizedOrderId = this.normalizeCancelOrderId(orderId)
      const pendingRequests = this.getPendingCancelRequests()
      const requestKey = `cancel_request_${normalizedOrderId}`

      if (pendingRequests[requestKey]) {
        delete pendingRequests[requestKey]
        localStorage.setItem('pendingCancelRequests', JSON.stringify(pendingRequests))
        console.log('已移除訂單', orderId, '的待處理取消請求')
      }

      this.clearSnoozedCancelRequest(normalizedOrderId)
      this.notifyCancelRequestTodosChanged()

      // 同時關閉對應的彈窗（如果存在）
      if (this.cancelRequestModals.has(normalizedOrderId)) {
        const modal = this.cancelRequestModals.get(normalizedOrderId)
        try {
          modal.destroy()
          this.cancelRequestModals.delete(normalizedOrderId)
          console.log(`已關閉訂單 ${normalizedOrderId} 的取消請求彈窗`)
        } catch (modalError) {
          console.error('關閉彈窗時發生錯誤:', modalError)
          // 如果 destroy 失敗，嘗試從 Map 中移除
          this.cancelRequestModals.delete(normalizedOrderId)
        }
      }
    } catch (error) {
      console.error('移除待處理取消請求失敗:', error)
    }
  }


  // 檢查並同步待處理的取消請求至待辦區
  async checkAndShowPendingCancelRequests() {
    this.cleanExpiredCancelRequests()
    this.notifyCancelRequestTodosChanged()
  }

  // 從後端獲取最新的客戶名稱
  async updateCustomerNameFromBackend(request) {
    try {
      // 調用後端 API 獲取訂單詳情
      const apiUrl = `/api/admin/orders/${request.orderId}/detail`
      const token = sessionStorage.getItem('admin_token')
      
      const response = await fetch(apiUrl, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }
      
      const orderData = await response.json()
      
      // 更新請求中的客戶名稱
      const newCustomerName = orderData.order?.username || request.customerName
      
      const updatedRequest = {
        ...request,
        customerName: newCustomerName
      }
      
      // 更新緩存中的客戶名稱
      this.savePendingCancelRequest(updatedRequest)
      
      return updatedRequest
    } catch (error) {
      console.error('從後端獲取客戶名稱失敗:', error)
      throw error
    }
  }

  // 顯示拒絕確認對話框
  showRejectConfirmModal(orderId, orderNumber, customerName, originalData = null) {
    Modal.confirm({
      title: '確認拒絕取消請求',
      content: h('div', [
        h('p', `確定要拒絕客戶的訂單取消請求嗎？`),
        h('p', `訂單編號：${orderNumber}`),
        h('p', { style: 'color: #ff4d4f; font-weight: bold;' }, '此操作將通知客戶取消請求已被拒絕')
      ]),
      okText: '確認拒絕',
      cancelText: '返回',
      okType: 'danger',
      onOk: () => {
        this.handleRejectCancelOrder(orderId, orderNumber, customerName)
      },
      onCancel: () => {
        // 返回時重新顯示原取消請求彈窗，避免「稍後通知」消失
        const pendingData = originalData
          || this.pendingCancelRequestData?.get(this.normalizeCancelOrderId(orderId))
          || this.getPendingCancelRequests()[`cancel_request_${this.normalizeCancelOrderId(orderId)}`]

        if (pendingData) {
          this.showOrderCancelRequestModal(pendingData)
        }
      }
    })
  }

  // 清理過期的取消請求
  cleanExpiredCancelRequests() {
    const pendingRequests = this.getPendingCancelRequests()
    const requestKeys = Object.keys(pendingRequests)
    const now = new Date()
    
    requestKeys.forEach(key => {
      const request = pendingRequests[key]
      const requestTime = new Date(request.savedAt || request.timestamp)
      const hoursDiff = (now - requestTime) / (1000 * 60 * 60)
      
      if (hoursDiff >= 24) { // 移除超過24小時的請求
        this.removePendingCancelRequest(request.orderId)
        console.log('已清理過期的取消請求:', request.orderNumber)
      }
    })
  }

  // 處理稍後通知
  async handleLaterNotification(orderId, orderNumber, customerName, modal) {
    console.log(`稍後通知訂單 ${orderNumber} 的取消請求`)

    this.closeCancelRequestModal(orderId, modal)
    this.markCancelRequestSnoozed(orderId)
    this.notifyCancelRequestTodosChanged()

    try {
      await this.postSnoozeCancelRequest(orderId, orderNumber)
    } catch (error) {
      console.error('同步稍後通知至其他管理員視窗失敗:', error)
    }

    notification.info({
      message: '已加入待辦',
      description: `訂單 ${orderNumber} 已加入取消訂單待辦區，可隨時點擊「立即處理」`,
      duration: 4,
      placement: 'topRight'
    })
  }

  // 處理拒絕取消訂單
  async handleRejectCancelOrder(orderId, orderNumber, customerName) {
    try {
      // 發送拒絕通知給前台用戶
      const response = await fetch(`/api/admin/orders/${orderId}/reject-cancel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('admin_token')}`
        },
        body: JSON.stringify({
          orderNumber: orderNumber,
          customerName: customerName,
          reason: '管理員拒絕取消訂單'
        })
      })

      const result = await response.json()

      if (result.success) {
        // 移除待處理的取消請求
        this.removePendingCancelRequest(orderId)
        this.clearSnoozedCancelRequest(orderId)
        this.notifyCancelRequestTodosChanged()

        notification.info({
          message: '📝 已拒絕取消請求',
          description: `已拒絕客戶 ${customerName} 的訂單 ${orderNumber} 取消請求，並已通知客戶`,
          duration: 5,
          placement: 'topRight'
        })
      } else {
        notification.error({
          message: '❌ 拒絕操作失敗',
          description: result.message || '拒絕取消請求時發生錯誤',
          duration: 8,
          placement: 'topRight'
        })
      }
    } catch (error) {
      console.error('拒絕取消請求失敗:', error)
      notification.error({
        message: '❌ 拒絕操作失敗',
        description: '網路錯誤，請稍後再試',
        duration: 8,
        placement: 'topRight'
      })
    }
  }


}

// 創建單例實例
const adminWebSocketService = new AdminWebSocketService()

export default adminWebSocketService